import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Axios from 'axios';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Spin,
  Button,
  message,
  Space,
  Switch,
  Popconfirm,
  Typography,
} from 'antd';
import {
  HolderOutlined,
  SaveOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  BookOutlined,
} from '@ant-design/icons';
// react-beautiful-dnd は保守終了 + React 18 StrictMode 非対応のため、後継フォーク @hello-pangea/dnd へ移行（API互換）
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { PageContainer, EmptyState } from '../../common';
import './ArticleListPage.css';

const { Text } = Typography;

// シリーズホーム (ArticleListPage)
// - URL: /article/:seriesCode
// - 閲覧（閲覧モード）: 新デザインのシリーズページ（左サイドバー目次 + イントロ/特徴/編セクション/CTA/著者）
// - role 9 のみ「編集モード」トグル可、ドラッグで記事を別カテゴリ・別編へ移動（既存機能を維持）
function ArticleListPage(props) {
  const params = useParams();
  const history = useHistory();
  const reduxUser = useSelector((state) => state.user);
  const userRole = reduxUser && reduxUser.userData && reduxUser.userData.role;

  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState(null);
  const [articles, setArticles] = useState([]); // 백엔드에서 받은 정렬 + episode_number 포함

  // 編集モード
  const [editMode, setEditMode] = useState(false);
  // 編集中の作業バッファ（編集モード時のみ使用）
  const [draftArticles, setDraftArticles] = useState([]);
  const [saving, setSaving] = useState(false);

  // ─────────────────────────────────
  // データ取得（:seriesCode は本ルートでは常に存在する）
  // role を渡すと、バックエンドが role 1/7 には公開記事のみ返す
  // ─────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const seriesCode = params.seriesCode;
      const res = await Axios.get(
        `/api/article/by-series/${seriesCode}?role=${userRole}`
      );
      if (res.data && res.data.success) {
        setSeries(res.data.series);
        setArticles(res.data.articles || []);
      } else {
        message.error('シリーズの取得に失敗しました');
      }
    } catch (err) {
      console.log(err);
      message.error('シリーズホームの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [params.seriesCode, userRole]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 編集モード ON のたびに draft を articles で初期化
  useEffect(() => {
    if (editMode) {
      setDraftArticles(articles.map((a) => ({ ...a })));
    }
  }, [editMode, articles]);

  // ─────────────────────────────────
  // 編・カテゴリのグループ化（編集モードのドラッグ用）
  // editions[] の順 → categories[] の順 → 各グループ内の position 順
  // category_code が null の記事は「カテゴリなし」グループ（編ヘッダー直下）に配置
  // ─────────────────────────────────
  const groupedView = useMemo(() => {
    if (!series) return [];
    const source = editMode ? draftArticles : articles;
    const editions = series.editions || [];
    const categories = series.categories || [];

    return editions.map((ed) => {
      // この編に属するカテゴリ（順序保持）
      const catsInEdition = categories.filter(
        (c) => c.edition_code === ed.edition_code
      );

      // カテゴリなしグループ（先頭に表示）
      const noCategoryArticles = source
        .filter(
          (a) => a.edition_code === ed.edition_code && a.category_code === null
        )
        .sort((a, b) => (a.position || 0) - (b.position || 0));

      const categoryGroups = catsInEdition.map((cat) => ({
        key: `cat_${cat.category_code}`,
        type: 'category',
        category: cat,
        articles: source
          .filter(
            (a) =>
              a.edition_code === ed.edition_code &&
              a.category_code === cat.category_code
          )
          .sort((a, b) => (a.position || 0) - (b.position || 0)),
      }));

      // 「カテゴリなし」がある編は先頭に配置
      const groups = [];
      if (noCategoryArticles.length > 0 || catsInEdition.length === 0) {
        groups.push({
          key: `nocat_${ed.edition_code}`,
          type: 'no_category',
          articles: noCategoryArticles,
        });
      }
      groups.push(...categoryGroups);

      return {
        edition: ed,
        groups,
        total: source.filter((a) => a.edition_code === ed.edition_code).length,
      };
    });
  }, [series, articles, draftArticles, editMode]);

  // 表示用の総合計
  const totalArticles = articles.length;

  // ─────────────────────────────────
  // 閲覧モード用のグループ化（編 → カテゴリ → 記事）
  // 公開フィルタ済みの articles を使い、記事が1件も無い編・カテゴリは除外する
  // ─────────────────────────────────
  const viewerSections = useMemo(() => {
    if (!series) return [];
    const editions = series.editions || [];
    const categories = series.categories || [];

    const sortByPosition = (list) =>
      [...list].sort((a, b) => (a.position || 0) - (b.position || 0));

    return editions
      .map((ed) => {
        const editionArticles = articles.filter(
          (a) => a.edition_code === ed.edition_code
        );
        if (editionArticles.length === 0) return null; // 記事の無い編は非表示

        const catsInEdition = categories.filter(
          (c) => c.edition_code === ed.edition_code
        );

        // カテゴリなし記事
        const noCategoryArticles = sortByPosition(
          editionArticles.filter((a) => !a.category_code)
        );

        // カテゴリ別（記事が1件以上あるカテゴリのみ）
        const categoryGroups = catsInEdition
          .map((cat) => ({
            category: cat,
            articles: sortByPosition(
              editionArticles.filter(
                (a) => a.category_code === cat.category_code
              )
            ),
          }))
          .filter((g) => g.articles.length > 0);

        return {
          edition: ed,
          count: editionArticles.length,
          noCategoryArticles,
          categoryGroups,
          // カテゴリを持つ編かどうか（持たない場合はフラットリスト）
          hasCategories: categoryGroups.length > 0,
        };
      })
      .filter(Boolean);
  }, [series, articles]);

  // 著者（先頭記事の instructor）
  const author = useMemo(() => {
    const first = articles[0];
    return (first && first.instructor) || null;
  }, [articles]);

  // 合計読了時間（reading_minutes の合計を時間/分へ整形）
  const totalReadingLabel = useMemo(() => {
    const totalMin = articles.reduce(
      (sum, a) => sum + (Number(a.reading_minutes) || 0),
      0
    );
    if (totalMin <= 0) return null;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0) return `約${h}時間${m}分`;
    return `約${m}分`;
  }, [articles]);

  // 2桁番号（01, 02 ...）
  const pad2 = (n) => String(n).padStart(2, '0');

  // サイドバー目次の記事リンク（下書きは管理者にのみ返ってくるため、その場合バッジを付与）
  const renderSidebarItem = (art) => (
    <li key={art._id}>
      <a href={`/article/${params.seriesCode}/${art._id}`}>
        {art.title}
        {art.status === 'draft' && (
          <span className="alp-sb-draft">下書き</span>
        )}
      </a>
    </li>
  );

  // 記事詳細への遷移
  const goToArticle = useCallback(
    (articleId) => {
      history.push(`/article/${params.seriesCode}/${articleId}`);
    },
    [history, params.seriesCode]
  );

  // ─────────────────────────────────
  // 編集モード: ドラッグ終了
  // - 同じドロップエリア内 = position 並べ替え
  // - 別ドロップエリア = edition_code/category_code 移動 + position 再採番
  // ─────────────────────────────────
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const src = result.source;
    const dst = result.destination;
    if (src.droppableId === dst.droppableId && src.index === dst.index) {
      return;
    }

    // droppableId 形式: "drop::{edition_code}::{category_code or 'null'}"
    const parseId = (id) => {
      const parts = id.split('::');
      const edition_code = parts[1];
      const category_code = parts[2] === 'null' ? null : parts[2];
      return { edition_code, category_code };
    };
    const srcInfo = parseId(src.droppableId);
    const dstInfo = parseId(dst.droppableId);

    // 移動対象の記事を特定（draft 内で同じグループの index 番目）
    const srcGroupArticles = draftArticles
      .filter(
        (a) =>
          a.edition_code === srcInfo.edition_code &&
          a.category_code === srcInfo.category_code
      )
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    const moving = srcGroupArticles[src.index];
    if (!moving) return;

    // dst グループの記事配列（移動対象を除外）
    const dstGroupArticles = draftArticles
      .filter(
        (a) =>
          a.edition_code === dstInfo.edition_code &&
          a.category_code === dstInfo.category_code &&
          a._id !== moving._id
      )
      .sort((a, b) => (a.position || 0) - (b.position || 0));

    // dst の指定 index に挿入
    const newDstGroup = [...dstGroupArticles];
    newDstGroup.splice(dst.index, 0, moving);

    // 編・カテゴリ情報を更新（移動先の edition/category に合わせる）
    // 名前も series メタから引いて非正規化を整合
    const dstEd = (series.editions || []).find(
      (e) => e.edition_code === dstInfo.edition_code
    );
    const dstCat =
      dstInfo.category_code === null
        ? null
        : (series.categories || []).find(
            (c) => c.category_code === dstInfo.category_code
          );

    // draft を更新（move + position 再採番 + edition/category 更新）
    const newDraft = draftArticles.map((a) => {
      // dst グループに含まれる記事（移動対象 + 既存）の position と edition/category を更新
      const idxInDst = newDstGroup.findIndex((x) => x._id === a._id);
      if (idxInDst !== -1) {
        return {
          ...a,
          edition_code: dstInfo.edition_code,
          edition_name: dstEd ? dstEd.edition_name : a.edition_name,
          category_code: dstInfo.category_code,
          category_name: dstCat ? dstCat.category_name : null,
          position: idxInDst,
        };
      }
      // src グループから抜ける記事の元グループは position 再採番
      if (
        a.edition_code === srcInfo.edition_code &&
        a.category_code === srcInfo.category_code &&
        a._id !== moving._id
      ) {
        const srcLeftover = srcGroupArticles.filter((x) => x._id !== moving._id);
        const idxInSrcLeft = srcLeftover.findIndex((x) => x._id === a._id);
        if (idxInSrcLeft !== -1) {
          return { ...a, position: idxInSrcLeft };
        }
      }
      return a;
    });

    setDraftArticles(newDraft);
  };

  // 保存
  const onSave = async () => {
    setSaving(true);
    try {
      // 変更があった記事のみ抽出
      const updates = draftArticles
        .filter((d) => {
          const orig = articles.find((a) => a._id === d._id);
          if (!orig) return false;
          return (
            orig.edition_code !== d.edition_code ||
            orig.category_code !== d.category_code ||
            orig.position !== d.position
          );
        })
        .map((d) => ({
          id: d._id,
          edition_code: d.edition_code,
          edition_name: d.edition_name,
          category_code: d.category_code,
          category_name: d.category_name,
          position: d.position,
        }));

      if (updates.length === 0) {
        message.info('変更はありません');
        setEditMode(false);
        setSaving(false);
        return;
      }

      const res = await Axios.patch('/api/article/reorder', { updates });
      if (res.data && res.data.success) {
        message.success(`${res.data.updated} 件の記事を更新しました`);
        setEditMode(false);
        // 再取得
        await fetchData();
      } else {
        message.error('保存に失敗しました');
      }
    } catch (err) {
      console.log(err);
      message.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    setDraftArticles([]);
    setEditMode(false);
  };

  // ─────────────────────────────────
  // 編集モードの記事カード描画（ドラッグ可能）
  // ─────────────────────────────────
  const renderEditCard = (art, indexInGroup) => {
    const cardInner = (
      <>
        <div className="alp-drag-handle" title="ドラッグして並べ替え">
          <HolderOutlined style={{ fontSize: 18 }} />
        </div>
        <div className="alp-episode-num">
          <div className="alp-episode-num-label">EP</div>
          <div className="alp-episode-num-value">
            {pad2(art.episode_number)}
          </div>
        </div>
        <div className="alp-card-body">
          <div className="alp-card-tags">
            {art.status === 'draft' && (
              <span className="alp-card-tag alp-card-tag--draft">下書き</span>
            )}
            {art.category_name && (
              <span className="alp-card-tag">{art.category_name}</span>
            )}
            {(art.tags || []).slice(0, 3).map((t) => (
              <span key={t} className="alp-card-tag alp-card-tag-sub">
                {t}
              </span>
            ))}
          </div>
          <h3 className="alp-card-title">{art.title}</h3>
          {art.summary && <p className="alp-card-desc">{art.summary}</p>}
        </div>
        <div className="alp-card-meta">
          <span className="alp-meta-row">{art.reading_time}</span>
        </div>
      </>
    );

    return (
      <Draggable draggableId={art._id} index={indexInGroup} key={art._id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`alp-article-card${
              snapshot.isDragging ? ' alp-dragging' : ''
            }`}
            style={{
              ...provided.draggableProps.style,
              gridTemplateColumns: '32px 64px 1fr auto',
            }}
          >
            {cardInner}
          </div>
        )}
      </Draggable>
    );
  };

  // ─────────────────────────────────
  // 閲覧モードの記事カード（横長・リンク）
  // ─────────────────────────────────
  const renderViewerCard = (art, edition) => (
    <a
      key={art._id}
      href={`/article/${art.series_code}/${art._id}`}
      className="alp-art-card"
    >
      <div className="alp-art-thumb">
        {art.thumbnail && <img src={art.thumbnail} alt="" />}
      </div>
      <div className="alp-art-body">
        <div className="alp-art-tags">
          {art.status === 'draft' && (
            <span className="alp-art-tag alp-art-tag--draft">下書き</span>
          )}
          {(edition && edition.edition_name) || art.edition_name ? (
            <span className="alp-art-tag">
              {(edition && edition.edition_name) || art.edition_name}
            </span>
          ) : null}
          {art.category_name && (
            <span className="alp-art-tag alp-art-tag--ghost">
              {art.category_name}
            </span>
          )}
        </div>
        <div className="alp-art-meta">
          {art.reading_time && (
            <span className="alp-mi">
              <ClockCircleOutlined />
              {art.reading_time}
            </span>
          )}
        </div>
        <h3 className="alp-art-title">{art.title}</h3>
        {art.sub_title && <p className="alp-art-sub">{art.sub_title}</p>}
        {art.summary && <p className="alp-art-summary">{art.summary}</p>}
      </div>
    </a>
  );

  // ─────────────────────────────────
  // メインレンダー
  // ─────────────────────────────────
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!series) {
    return (
      <div style={{ padding: 40 }}>
        <EmptyState message="シリーズが登録されていません" />
      </div>
    );
  }

  // パンくず（共通）
  const breadcrumb = (
    <nav className="alp-breadcrumb" aria-label="パンくず">
      <a href="/">Home</a>
      <span className="alp-breadcrumb-sep">›</span>
      <a href="/article">記事一覧</a>
      <span className="alp-breadcrumb-sep">›</span>
      <span className="alp-breadcrumb-current">{series.series_name}</span>
    </nav>
  );

  // 編集モード切替バー（role 9 のみ）
  const editToggleBar = userRole === 9 && (
    <div className="alp-edit-bar">
      <Switch
        checked={editMode}
        onChange={(v) => setEditMode(v)}
        checkedChildren="編集中"
        unCheckedChildren="閲覧"
      />
      <Text type="secondary" style={{ flex: 1 }}>
        {editMode
          ? 'ドラッグでカードを並べ替え／別の編・カテゴリへ移動できます'
          : '記事の表示順を変更するには「編集モード」をONにしてください（管理者専用）'}
      </Text>
      {editMode && (
        <Space>
          <Popconfirm
            title="変更を破棄しますか？"
            onConfirm={onCancel}
            okText="破棄"
            cancelText="キャンセル"
          >
            <Button icon={<CloseCircleOutlined />}>キャンセル</Button>
          </Popconfirm>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={onSave}
          >
            保存
          </Button>
        </Space>
      )}
    </div>
  );

  // ページ外郭の上下余白（PageContainer は左右 padding のみ持つため上下を補う）
  const containerStyle = { paddingTop: 'var(--space-6)', paddingBottom: 'var(--space-12)' };

  // ═══════════════════════════════════════
  // 編集モード（role 9・編集中 ON）: 既存のドラッグ並べ替え UI を維持
  // ═══════════════════════════════════════
  if (userRole === 9 && editMode) {
    return (
      <PageContainer className="alp-root" style={containerStyle}>
        {breadcrumb}
        {editToggleBar}

        <DragDropContext onDragEnd={onDragEnd}>
          {groupedView.map((sec) => (
            <section
              key={sec.edition.edition_code}
              className="alp-edit-section"
            >
              <div className="alp-edit-section-header">
                <h2 className="alp-edit-section-title">
                  {sec.edition.edition_name}
                </h2>
                {sec.edition.edition_subtitle && (
                  <span className="alp-edit-section-subtitle">
                    {sec.edition.edition_subtitle}
                  </span>
                )}
                <span className="alp-edit-section-count">
                  全 {sec.total} 件
                </span>
              </div>

              {sec.groups.map((g) => {
                const droppableId = `drop::${sec.edition.edition_code}::${
                  g.type === 'no_category' ? 'null' : g.category.category_code
                }`;
                return (
                  <div key={g.key}>
                    {g.type === 'category' && (
                      <h3 className="alp-edit-category-header">
                        {g.category.category_name}
                        {g.category.category_subtitle && (
                          <span className="alp-edit-category-subtitle">
                            {g.category.category_subtitle}
                          </span>
                        )}
                      </h3>
                    )}
                    <Droppable droppableId={droppableId}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`alp-edit-list alp-droppable${
                            snapshot.isDraggingOver ? ' alp-drag-over' : ''
                          }`}
                        >
                          {g.articles.length === 0 && (
                            <div className="alp-drop-hint">
                              （ここへドロップして移動）
                            </div>
                          )}
                          {g.articles.map((art, i) => renderEditCard(art, i))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </section>
          ))}
        </DragDropContext>

        {totalArticles === 0 && (
          <EmptyState
            message="記事がまだ登録されていません"
            style={{ padding: 40 }}
          />
        )}
      </PageContainer>
    );
  }

  // ═══════════════════════════════════════
  // 閲覧モード（全ロール共通 / role 9 の閲覧 OFF）: 新デザイン
  // ═══════════════════════════════════════
  return (
    <PageContainer className="alp-root" style={containerStyle}>
      {breadcrumb}
      {editToggleBar}

      <div className="alp-layout-grid">
        {/* 左サイドバー: 目次（折り畳み） */}
        <aside className="alp-sidebar alp-block" aria-label="本シリーズについて">
          <div className="alp-sb-head">
            <div className="alp-sb-head-title">本シリーズについて</div>
            <div className="alp-sb-head-meta">
              全{totalArticles}回
              {totalReadingLabel ? `・${totalReadingLabel}` : ''}
            </div>
          </div>

          {viewerSections.map((sec, idx) => (
            <details
              className="alp-sb-part"
              key={sec.edition.edition_code}
              open
            >
              <summary className="alp-sb-part-summary">
                <span className="alp-sb-num">{pad2(idx + 1)}</span>
                <span className="alp-sb-part-name">
                  {sec.edition.edition_name}
                </span>
                <span className="alp-sb-count">{sec.count}回</span>
                <span className="alp-sb-chevron" aria-hidden="true" />
              </summary>

              {sec.hasCategories ? (
                <>
                  {sec.noCategoryArticles.length > 0 && (
                    <ul className="alp-sb-list">
                      {sec.noCategoryArticles.map(renderSidebarItem)}
                    </ul>
                  )}
                  {sec.categoryGroups.map((g) => (
                    <React.Fragment key={g.category.category_code}>
                      <div className="alp-sb-subgroup">
                        {g.category.category_name}
                      </div>
                      <ul className="alp-sb-list">
                        {g.articles.map(renderSidebarItem)}
                      </ul>
                    </React.Fragment>
                  ))}
                </>
              ) : (
                <ul className="alp-sb-list">
                  {sec.noCategoryArticles.map(renderSidebarItem)}
                </ul>
              )}
            </details>
          ))}
        </aside>

        {/* 右カラム: コンテンツスタック */}
        <div className="alp-content-stack">
          {/* イントロ（紹介文） */}
          <section className="alp-intro alp-block">
            <p className="alp-intro-eyebrow">
              BALLON INSIGHTS
              {/* 後半は固定文言「SERIES｜全N回」+ 記事数カウント（series_subtitle 参照は廃止） */}
              <span className="alp-eb-sep" />
              SERIES｜全{totalArticles}回
            </p>
            <h1 className="alp-intro-title">{series.series_name}</h1>
            {series.series_description &&
              series.series_description
                .split('\n')
                .filter((line) => line.trim() !== '')
                .map((line, i) => <p key={i}>{line}</p>)}
          </section>

          {/* ヒーロー（サムネイル） */}
          {series.thumbnail && (
            <div className="alp-hero alp-block">
              <img src={series.thumbnail} alt={series.series_name} />
            </div>
          )}

          {/* 本シリーズの特徴 */}
          {Array.isArray(series.features) && series.features.length > 0 && (
            <section
              className="alp-benefits alp-block"
              aria-label="本シリーズの特徴"
            >
              <h2 className="alp-benefits-head">本シリーズの特徴</h2>
              <div className="alp-benefits-grid">
                {series.features.map((f, i) => (
                  <div className="alp-benefit" key={i}>
                    <h3 className="alp-benefit-title">{f.feature_title}</h3>
                    {f.feature_description && (
                      <p className="alp-benefit-desc">
                        {f.feature_description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 編セクション */}
          {viewerSections.map((sec, idx) => (
            <section
              className="alp-part alp-block"
              id={`part-${sec.edition.edition_code}`}
              key={sec.edition.edition_code}
            >
              <div className="alp-part-bar">
                <div className="alp-part-bar-num">{pad2(idx + 1)}</div>
                <div className="alp-part-bar-title">
                  {sec.edition.edition_name}
                  <span className="alp-part-bar-count">全{sec.count}回</span>
                </div>
              </div>
              {sec.edition.edition_subtitle && (
                <p className="alp-part-tagline">
                  {sec.edition.edition_subtitle}
                </p>
              )}
              {sec.edition.edition_description && (
                <p className="alp-part-lead">
                  {sec.edition.edition_description}
                </p>
              )}

              {sec.hasCategories ? (
                <>
                  {sec.noCategoryArticles.length > 0 && (
                    <div className="alp-article-list">
                      {sec.noCategoryArticles.map((art) =>
                        renderViewerCard(art, sec.edition)
                      )}
                    </div>
                  )}
                  {sec.categoryGroups.map((g) => (
                    <React.Fragment key={g.category.category_code}>
                      <h3 className="alp-subgroup-head">
                        {g.category.category_name}
                        <span className="alp-subgroup-count">
                          全{g.articles.length}回
                        </span>
                      </h3>
                      {g.category.category_subtitle && (
                        <p className="alp-subgroup-sub">
                          {g.category.category_subtitle}
                        </p>
                      )}
                      <div className="alp-article-list">
                        {g.articles.map((art) =>
                          renderViewerCard(art, sec.edition)
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </>
              ) : (
                <div className="alp-article-list">
                  {sec.noCategoryArticles.map((art) =>
                    renderViewerCard(art, sec.edition)
                  )}
                </div>
              )}
            </section>
          ))}

          {/* CTA バナー */}
          {articles.length > 0 && (
            <div className="alp-cta-banner">
              <div className="alp-cta-icon">
                <BookOutlined />
              </div>
              <div className="alp-cta-text">
                <div className="alp-cta-title">
                  まずは最初の編から、シリーズの全体像をつかみましょう
                </div>
                <p className="alp-cta-desc">
                  順番に読み進めることで、シリーズ全体の理解が深まります。
                </p>
              </div>
              <span
                className="alp-cta-button"
                role="button"
                tabIndex={0}
                onClick={() => goToArticle(articles[0]._id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    goToArticle(articles[0]._id);
                  }
                }}
              >
                {viewerSections[0] && viewerSections[0].edition
                  ? `${viewerSections[0].edition.edition_name}を読む`
                  : '最初の記事を読む'}
                <span className="alp-cta-button-arrow">›</span>
              </span>
            </div>
          )}

          {/* 著者プロフィール */}
          {author && (
            <footer className="alp-editor alp-block">
              <div className="alp-editor-head">本シリーズの著者</div>
              <div className="alp-editor-body">
                <img
                  className="alp-editor-icon"
                  src={author.photo_url || ''}
                  alt={author.name || ''}
                />
                <div className="alp-editor-content">
                  <div className="alp-editor-name">{author.name}</div>
                  {author.title && (
                    <div className="alp-editor-title">{author.title}</div>
                  )}
                  {author.profile && (
                    <div className="alp-editor-bio">
                      <p>{author.profile}</p>
                    </div>
                  )}
                </div>
              </div>
            </footer>
          )}

          {/* 記事ゼロ件の空状態 */}
          {articles.length === 0 && (
            <EmptyState
              message="この シリーズ にはまだ記事がありません"
              style={{ padding: 40 }}
            />
          )}
        </div>
      </div>
    </PageContainer>
  );
}

export default ArticleListPage;
